import React, { useState, useRef } from 'react';
import _ from 'lodash';
import { Field, ErrorMessage } from 'formik';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { colorStyle } from 'grommet-styles';
import { CircleInformation } from 'grommet-icons';

import { Box, Stack, Paragraph, Text, TextInput } from 'components/Wrapped';

export const PlaceHolder = styled(Box)`
  ${(props) =>
    props.hasFocus &&
    css`
      transform: scale(0.75) translateY(-60%);
      transform-origin: left;
    `} transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const TextField = styled(TextInput)`
  backdrop-filter: blur(120px);
  border-radius: 16px;
  color-scheme: dark;
  font-size: 14px;
  padding: 33px 16px 13px;

  &:focus {
    border-color: white;
  }

  ${(props) =>
    !props.isTransparent &&
    css`
      background-color: rgba(255, 255, 255, 0.12);
      border: 1px solid #38444a;
    `}

  ${(props) =>
    props.hasFocus &&
    css`
      box-shadow: none;
    `}

  ${(props) =>
    props.hasAddonStart &&
    css`
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    `}

    ${(props) =>
      props.hasAddonEnd &&
      css`
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      `}


  ${(props) => props.hasErrors && colorStyle('border-color', 'askColor', props.theme)}
`;

const Addon = ({ background = 'background-5', content = '', isEnd = false, ...rest }) => (
  <Box
    tabIndex="-1"
    round={{ corner: isEnd ? 'right' : 'left', size: 'xxsmall' }}
    pad={{ horizontal: 'small' }}
    background={background}
    justify="center"
    flex="grow"
    {...rest}
  >
    {typeof content === 'string' ? <Paragraph>{content}</Paragraph> : content}
  </Box>
);

export const StlyedAddon = styled(Addon)`
  ${(props) =>
    props.onClick &&
    css`
      cursor: pointer;
    `};
`;

export const StyledTextInput = ({ addonStart, addonEnd, ...rest }) => {
  const [isFocused, setIsFocused] = useState(false);
  const searchInput = useRef(null);

  function focus() {
    // Explicitly focus the text input using the raw DOM API
    // Note: we're accessing "current" to get the DOM node
    searchInput.current.focus();
  }
  return (
    <Box direction="row" pad="none" round={false}>
      {addonStart && <StlyedAddon {...addonStart} />}
      <Box round={false} pad="none" fill={true}>
        <TextField
          ref={searchInput}
          focusIndicator={false}
          hasAddonStart={!_.isEmpty(addonStart)}
          hasAddonEnd={!_.isEmpty(addonEnd)}
          hasFocus={isFocused}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
          }}
          {...rest}
        />
      </Box>
      {addonEnd && <StlyedAddon onClick={focus} {...addonEnd} isEnd={true} />}
    </Box>
  );
};

const WrappedNewTextInput = ({
  name,
  placeholder,
  type,
  size,
  persistentPlaceholder,
  addonStart,
  addonEnd,
  value,
  margin = { bottom: '10px' },
  bottomPadding = '38px',
  isTransparent = false,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Box pad={{ bottom: bottomPadding }} margin={margin} round={false} style={{ position: 'relative' }}>
      <Field name={name}>
        {({ field, form: { handleBlur, touched, errors } }) => (
          <Box direction="row" pad="none" round={false}>
            {addonStart && <StlyedAddon {...addonStart} />}
            <Box round={false} pad="none" fill={true}>
              <Stack anchor="left" interactiveChild="first">
                <TextField
                  {...field}
                  {...rest}
                  isTransparent={isTransparent}
                  type={type}
                  onFocus={() => setIsFocused(true)}
                  onBlur={(e) => {
                    handleBlur(e);
                    setIsFocused(false || !_.isEmpty(field.value));
                  }}
                  hasErrors={_.get(touched, field.name) && _.get(errors, field.name)}
                  hasFocus={isFocused}
                  focusIndicator={false}
                  hasAddonStart={!_.isEmpty(addonStart)}
                  hasAddonEnd={!_.isEmpty(addonEnd)}
                  placeholder={!persistentPlaceholder && placeholder}
                  value={value}
                />
                {persistentPlaceholder && (
                  <PlaceHolder
                    hasFocus={isFocused || field.value}
                    pad="xsmall"
                    margin={{ left: 'small' }}
                    round={false}
                    background="transparent"
                  >
                    <Text color="#B4B6B7" size="small" weight={500}>
                      {placeholder}
                    </Text>
                  </PlaceHolder>
                )}
              </Stack>
            </Box>
            {addonEnd && <StlyedAddon {...addonEnd} isEnd={true} />}
          </Box>
        )}
      </Field>
      <ErrorMessage
        name={name}
        render={(msg) => (
          <Box pad="none" align="center" direction="row" gap="4px" style={{ position: 'absolute', top: '73px' }}>
            <CircleInformation color="#E56150" size="16px" />
            <Text color="white" size="13px">
              {msg}
            </Text>
          </Box>
        )}
      />
    </Box>
  );
};

WrappedNewTextInput.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string.isRequired,
  persistentPlaceholder: PropTypes.bool,
};

WrappedNewTextInput.defaultProps = {
  type: 'text',
  persistentPlaceholder: true,
};

export default WrappedNewTextInput;
